import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { currenciesFromMonies } from '@klear/money'
import { ROUTE_DISBURSEMENTS } from '@klear/constants'
import { trpc } from '@klear/ui/lib/trpc'
import { useDisbursableAdvances, useDisbursementMutations } from '@klear/ui/hooks/use-disbursements'
import isFinite from 'lodash/isFinite'
import toNumber from 'lodash/toNumber'
import { PAYMENT_TYPE_DISBURSEMENT_ADVANCE } from '@klear/payment/constants'
import {
  DISBURSEMENT_TYPE,
  getDisbursementStepOrder,
  type DisbursementType,
  type DisbursementStepId
} from '@/app/disbursements/steps'

export type DisbursementState = {
  disbursementType: DisbursementType
  traderId: number | null
  selectedAdvanceIds: number[]
  paymentIds: number[]
}

const defaultState: DisbursementState = {
  disbursementType: DISBURSEMENT_TYPE.ADVANCE,
  traderId: null,
  selectedAdvanceIds: [],
  paymentIds: []
}

export type DisbursementOutletContext = {
  state: DisbursementState
  setState: React.Dispatch<React.SetStateAction<DisbursementState>>
  stepOrder: DisbursementStepId[]
  disbursableAdvances: InvoiceOffer[]
  isDisbursableLoading: boolean
  hasMixedCurrency: boolean
  disburse: () => Promise<void>
  isDisbursing: boolean
}

export type DisbursementStepCtx = DisbursementOutletContext

const DisbursementContext = createContext<DisbursementOutletContext | null>(null)

function buildInitialState(searchParams: URLSearchParams): DisbursementState {
  const tRaw = searchParams.get('traderId')
  const tid = tRaw ? toNumber(tRaw) : NaN
  const traderId = isFinite(tid) && tid > 0 ? tid : null
  return { ...defaultState, traderId }
}

export function DisbursementWizardLayout({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<DisbursementState>(() => buildInitialState(searchParams))

  const stepOrder = useMemo(() => getDisbursementStepOrder(state.disbursementType), [state.disbursementType])

  const hasTrader = state.traderId != null
  const { disbursableAdvances, isLoading: isDisbursableLoading } = useDisbursableAdvances(state.traderId, hasTrader)

  const selectedPrices = useMemo(() => {
    const selected = new Set(state.selectedAdvanceIds)
    return disbursableAdvances.filter((a) => selected.has(a.id)).map((a) => a.purchasePrice)
  }, [disbursableAdvances, state.selectedAdvanceIds])
  const hasMixedCurrency = useMemo(() => currenciesFromMonies(selectedPrices).length > 1, [selectedPrices])

  const utils = trpc.useUtils()
  const { disburseAdvances, isDisbursing } = useDisbursementMutations()

  const disburse = useCallback(async () => {
    if (state.selectedAdvanceIds.length === 0) return
    try {
      const result = await disburseAdvances({ disbursableIds: state.selectedAdvanceIds })
      const ids = result?.paymentIds ?? []
      setState((prev) => ({ ...prev, paymentIds: ids }))
      await Promise.all([
        utils.disbursement.listDisbursableAdvances.invalidate(),
        utils.disbursement.listDisbursementPayments.invalidate(),
        ...state.selectedAdvanceIds.map((id) => utils.invoiceOffer.get.invalidate({ invoiceOfferId: id }))
      ])
      const params = new URLSearchParams({ filter: PAYMENT_TYPE_DISBURSEMENT_ADVANCE })
      navigate(`/${ROUTE_DISBURSEMENTS}?${params.toString()}`)
    } catch {}
  }, [state.selectedAdvanceIds, disburseAdvances, utils, navigate])

  const contextValue = useMemo<DisbursementOutletContext>(
    () => ({
      state,
      setState,
      stepOrder,
      disbursableAdvances,
      isDisbursableLoading: hasTrader && isDisbursableLoading,
      hasMixedCurrency,
      disburse,
      isDisbursing
    }),
    [state, stepOrder, disbursableAdvances, hasTrader, isDisbursableLoading, hasMixedCurrency, disburse, isDisbursing]
  )

  return <DisbursementContext.Provider value={contextValue}>{children}</DisbursementContext.Provider>
}

export function useDisbursementOutletContext(): DisbursementOutletContext {
  const ctx = useContext(DisbursementContext)
  if (!ctx) throw new Error('useDisbursementOutletContext must be used within DisbursementWizardLayout')
  return ctx
}
